import { useEffect } from 'react'

import { generateApplicationUrl, toggleArray } from '@domain-group/javascript-utils'
import { useSetState } from '@domain-group/react-hooks'

import { findUsersUsingAgentIds } from '@functions/agent'
import mapContactsAndUserObjects from '@functions/mapContactsAndUserObjects'

import useArtworks from '@hooks/useArtworks'
import useCurrentUser from '@hooks/useCurrentUser'
import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import * as campaignActions from '@redux/modules/campaign'
import * as conversationActions from '@redux/modules/conversation'
import * as userActions from '@redux/modules/user'

import type { AppDispatch } from '@redux/store'
import type { EntitiesState } from '@redux/entities'
import type { ArtworkModel, CampaignModel, UserModel } from '@models/types'
import useReduxAction from './useReduxAction'
import { generateUrls } from './useCampaign'

type ShareMode = 'email' | 'link'

type DefaultState = {
  message: string,
  selectedArtworkIds: number[],
  selectedUserIds: number[],
  shareMode: ShareMode,
  subject: string,
}

const defaultState: DefaultState = {
  message: 'Please review artworks',
  selectedArtworkIds: [],
  selectedUserIds: [],
  shareMode: 'email',
  subject: '',
}

type SetState = (state: Partial<typeof defaultState>) => void

type BulkShareArtworkParams = {
  campaign: CampaignModel,
  dispatch: AppDispatch,
  message: string,
  selectedArtworkIds: number[],
  selectedUserIds: number[],
  subject: string,
}

const bulkShareArtwork = (params: BulkShareArtworkParams) => {
  const { bulkShareArtworks: bulkShareFn } = campaignActions
  const {
    campaign, dispatch, message, selectedArtworkIds, selectedUserIds, subject,
  } = params

  const { artworkReviewerUrl } = generateUrls(campaign, { artworkIds: selectedArtworkIds })

  // @ts-expect-error TS2339
  const { applicationUrlWithHash } = generateApplicationUrl({
    applicationKey: 'realhub',
    // @ts-expect-error TS2322
    environment: process.env.REACT_APP_APPLICATION_ENV,
    hash: artworkReviewerUrl,
  })

  return dispatch(bulkShareFn({
    artworkIds: selectedArtworkIds,
    campaign,
    message,
    subject,
    url: applicationUrlWithHash,
    userIds: selectedUserIds,
  }))
}

type CreateArtworkConversationParams = {
  artwork: ArtworkModel,
  campaign: CampaignModel,
  dispatch: AppDispatch,
  currentUser: UserModel,
  orderItems: EntitiesState['orderItems'],
}

const createArtworkConversation = (params: CreateArtworkConversationParams) => {
  const {
    artwork,
    campaign,
    currentUser: {
      id: userId,
      email,
    },
    dispatch,
    orderItems,
  } = params

  const { createSubjectConversation: createFn } = conversationActions

  const orderItem = orderItems[artwork.order_item_id] || {}

  const conversation = {
    agency_id: campaign.agency_id,
    campaign_id: campaign.id,
    subscribers: [{ user_id: userId, email }],
    subject_id: artwork.id,
    subject_type: 'Artwork',
    title: `Artwork for ${orderItem.title || `Order Item #${artwork.order_item_id}`}`,
    comment: {
      body: 'Please Review',
      public: false,
    },
  }

  // @ts-expect-error TS2345
  return dispatch(createFn(conversation, { only_if_stale: true }))
}

type PresignUrlParams = {
  dispatch: AppDispatch,
  user: UserModel,
}

const presignUrl = (params: PresignUrlParams) => {
  const { dispatch, user } = params
  const { presignUrl: presignFn } = userActions

  // @ts-expect-error TS2345
  return dispatch(presignFn(user, 'REPLACE_URL', { application_key: 'realhub' }))
}

type ValidateIsShareableParams = {
  selectedArtworkIds: number[],
  selectedUserIds: number[],
  message: string,
}

const validateIsShareable = (params: ValidateIsShareableParams) => {
  const { selectedArtworkIds, selectedUserIds, message } = params

  if (!selectedArtworkIds.length) return false
  if (!selectedUserIds.length) return false
  return !!message
}

type SelectArtworkParams = {
  artwork: ArtworkModel,
  campaign: CampaignModel,
  currentUser: UserModel,
  orderItems: EntitiesState['orderItems'],
  dispatch: AppDispatch,
  selectedArtworkIds: number[],
  setState: SetState,
}

const selectArtwork = (params: SelectArtworkParams) => {
  const {
    artwork,
    campaign,
    currentUser,
    dispatch,
    orderItems,
    selectedArtworkIds,
    setState,
  } = params

  const selectedIds = [...selectedArtworkIds]

  const index = selectedIds.indexOf(artwork.id)

  // Remove
  if (index !== -1){
    selectedIds.splice(index, 1)
  }

  // Add
  if (index === -1){
    selectedIds.push(artwork.id)

    createArtworkConversation({
      artwork,
      dispatch,
      campaign,
      currentUser,
      orderItems,
    })
  }

  setState({ selectedArtworkIds: selectedIds })
}

type UseBulkShareArtworksOptions = {
  campaign: CampaignModel,
}

function useBulkShareArtworks(options: UseBulkShareArtworksOptions) {
  const { campaign } = options

  const [state, setState] = useSetState(defaultState)
  const {
    message, selectedArtworkIds, selectedUserIds, shareMode, subject,
  } = state

  const dispatch = useDispatch()

  const { currentUser } = useCurrentUser()

  const entities = useSelector(reduxState => reduxState.entities)
  const { images, orderItems, users } = entities

  const agentUsers = findUsersUsingAgentIds({ agentIds: campaign.agent_ids, users })

  // Artworks
  const artworksPayload = useArtworks({
    filters: {
      campaign_id: campaign.id,
    },
    performHttpRequests: false,
  })
  const { filteredArtworks } = artworksPayload

  // Users
  const entityKey = `Campaign${campaign.id}-users`
  useReduxAction(
    'campaigns',
    'loadCampaignUsers',
    {
      entityKey,
      include_image_sizes: true,
      include_user_association: true,
      include_user_image: true,
      include_user_role: true,
      include_user_type: true,
    },
    [campaign.id],
    {
      dispatchAction: (action, requestOptions) => action(campaign.id, requestOptions),
      // @ts-expect-error TS2322
      shouldPerformFn: ({ loadedForKeys }) => campaign.id && !loadedForKeys.includes(entityKey),
    },
  )

  const { artworkReviewerPublicUrl, artworkReviewerUrl } = generateUrls(campaign, { artworkIds: selectedArtworkIds })

  // @ts-expect-error TS2339
  const { applicationUrlWithHash: publicUrl } = generateApplicationUrl({
    applicationKey: 'realhub',
    // @ts-expect-error TS2322
    environment: process.env.REACT_APP_APPLICATION_ENV,
    hash: artworkReviewerPublicUrl,
  })

  // @ts-expect-error TS2339
  const { applicationUrlWithHash: privateUrl } = generateApplicationUrl({
    applicationKey: 'realhub',
    // @ts-expect-error TS2322
    environment: process.env.REACT_APP_APPLICATION_ENV,
    hash: artworkReviewerUrl,
  })

  const contacts = mapContactsAndUserObjects({ images, users })

  // Set Subject
  useEffect(() => {
    if (campaign.address){
      setState({
        subject: `Artwork Review - ${campaign.address}`,
      })
    }
  }, [campaign.address])

  // Set Default Users
  useEffect(() => {
    if (agentUsers.length){
      // @ts-expect-error TS18048
      setState({ selectedUserIds: agentUsers.map(user => user.id) })
    }
  }, [agentUsers.length])

  const selectedUsers = selectedUserIds.map(userId => users[userId])
    .filter(u => u)

  const hasSelectedUsers = !!selectedUsers.length

  const canShareViaEmail = shareMode === 'email'
    && validateIsShareable({ message, selectedArtworkIds, selectedUserIds })

  return {
    agentUsers,
    artworkReviewUrls: {
      privateUrl,
      publicUrl,
    },
    callbacks: {
      bulkShareArtwork: () => bulkShareArtwork({
        campaign,
        dispatch,
        message,
        selectedArtworkIds,
        selectedUserIds,
        subject,
      }),
      dispatch,
      prepareUrl: (presignedUrl: string) => presignedUrl.replace('REPLACE_URL', privateUrl),
      presignUrl: (user: UserModel) => presignUrl({ dispatch, user }),
      selectArtwork: (artwork: ArtworkModel) => selectArtwork({
        artwork,
        campaign,
        // @ts-expect-error TS2322
        currentUser,
        dispatch,
        selectedArtworkIds,
        setState,
        orderItems,
      }),
      selectUserId: (userId: number) => {
        setState({ selectedUserIds: toggleArray(selectedUserIds, userId) })
      },
      setShareMode: (mode: ShareMode) => setState({ shareMode: mode }),
      setState,
    },
    campaign,
    canShareViaEmail,
    contacts,
    entities,
    filteredArtworks,
    hasSelectedUsers,
    message,
    selectedArtworkIds,
    selectedUsers,
    shareMode,
    subject,
  }
}

export type BulkShareArtworksPayload = ReturnType<typeof useBulkShareArtworks>

export default useBulkShareArtworks
